import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { verifyCaptcha } from "@/services/api/Master";
import { subscribe } from "@/services/api/Newsletter";
import { CheckCircle, CircleNotch, EnvelopeSimple } from "@phosphor-icons/react";
import { useReCaptcha } from "next-recaptcha-v3";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

const NewsletterArchiveSubscribe = ({ defaultEmail = "" }) => {
  const [loadingSubscribe, setLoadingSubscribe] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { executeRecaptcha } = useReCaptcha();

  const handleSubscribe = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        setLoadingSubscribe(true);

        const email = event.target.email.value;
        const first_name = event.target.first_name.value;
        const last_name = event.target.last_name.value;
        const thisYear = new Date().getFullYear();

        const token = await executeRecaptcha("newsletter_archive_subscribe");
        const response = await verifyCaptcha(token);

        if (response.data.score <= 0.5) {
          toast.error("Captcha verification failed. Please try again.");
          return;
        }

        const payload = {
          email,
          tags: ["Customer", String(thisYear), "Web"],
        };

        if (first_name) payload.first_name = first_name;
        if (last_name) payload.last_name = last_name;

        const { data } = await subscribe(payload);

        // A contact coming back from unsubscribed/bounced is only accepted via
        // double opt-in, so don't claim they are subscribed until they confirm.
        if (data?.status === "pending") {
          toast.success(
            data.message ||
              "Almost there — check your inbox to confirm your subscription."
          );
        } else {
          toast.success("You are successfully subscribed to our newsletter!");
        }

        event.target.reset();
        setIsSuccess(true);
      } catch (error) {
        const data = error.response?.data;

        if (data?.message === "User is already subscribed.") {
          toast(data.message);
        } else if (data?.reason === "forgotten_email") {
          // Mailchimp will not take this address back through the API under any
          // status, so telling them to try again would be false advice.
          toast.error(data.message, { duration: 8000 });
        } else {
          toast.error("Subscription failed. Please try again.");
        }
      } finally {
        setLoadingSubscribe(false);
      }
    },
    [executeRecaptcha]
  );

  return (
    <div className='max-w-[600px] mx-auto mt-6 lg:mt-8'>
      <div className='p-5 border rounded-2xl lg:p-6 border-emerald-500/30 bg-gradient-to-br from-emerald-600/25 via-emerald-700/10 to-transparent'>
        {isSuccess ? (
          <div className='flex flex-col items-center gap-2 py-4 text-center'>
            <CheckCircle
              weight='fill'
              className='text-3xl text-emerald-400'
            />
            <p className='text-lg font-semibold lg:text-xl font-display'>
              You&apos;re in! Check your inbox to confirm.
            </p>
            <p className='text-sm opacity-60'>
              Your next issue lands straight in your inbox every week.
            </p>
          </div>
        ) : (
          <>
            <div className='flex items-start gap-3'>
              <span className='hidden p-2 rounded-lg lg:inline-flex bg-emerald-500/20 text-emerald-300'>
                <EnvelopeSimple weight='bold' className='text-xl' />
              </span>
              <div>
                <p className='text-base font-semibold leading-tight tracking-tight lg:text-xl font-display'>
                  {`Plug into MENA's core. Join the growth circle of 10,000+ startups and 800+ VCs.`}
                </p>
                <p className='mt-1.5 text-xs leading-relaxed lg:text-sm opacity-60'>
                  {`Deep dives into local deal flow, exclusive stories from MENA's startup ecosystem, and the hottest tech trends.`}
                </p>
              </div>
            </div>

            <form
              onSubmit={handleSubscribe}
              disabled={loadingSubscribe}
              className='grid grid-cols-12 gap-2.5 mt-4 lg:gap-3'
            >
              <Input
                id='archive_first_name'
                name='first_name'
                type='text'
                placeholder='First Name'
                disabled={loadingSubscribe}
                className='col-span-6 rounded-lg bg-white/10 border-white/10 placeholder:text-white/40'
              />
              <Input
                id='archive_last_name'
                name='last_name'
                type='text'
                placeholder='Last Name'
                disabled={loadingSubscribe}
                className='col-span-6 rounded-lg bg-white/10 border-white/10 placeholder:text-white/40'
              />
              <Input
                id='archive_email'
                name='email'
                type='email'
                required
                defaultValue={defaultEmail}
                placeholder='Email address'
                disabled={loadingSubscribe}
                className='col-span-12 rounded-lg sm:col-span-8 bg-white/10 border-white/10 placeholder:text-white/40'
              />
              <Button
                type='submit'
                disabled={loadingSubscribe}
                className='col-span-12 text-sm sm:col-span-4 bg-emerald-500 text-white hover:bg-emerald-500/90'
              >
                {loadingSubscribe && (
                  <CircleNotch
                    weight='bold'
                    className='mr-2 text-base animate-spin'
                  />
                )}
                {loadingSubscribe ? "Loading..." : "Subscribe"}
              </Button>
            </form>

            <p className='mt-2.5 text-xs opacity-40'>
              No charge. Unsubscribe anytime.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default NewsletterArchiveSubscribe;
